import {
  ExtranetCustomerGroupDto,
  GetExtranetCustomerGroupQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacPortalTooltip, PharmacSearch, PharmacText } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { CustomerGroupsFilters } from '../../components/customer-groups/CustomerGroupsFilters'
import { CustomerGroupsListItem } from '../../components/customer-groups/CustomerGroupsListItem'
import {
  GroupSettingFilters,
  mapCustomerGroupSearchParamsToQueryParams,
} from '../../components/customer-groups/util/query-parameter-mapping'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { api } from '../../services/HttpService'

export const __customerCustomerGroupsQueryKeyFactory = (...params: any[]) => [
  '__getCustomerCustomerGroups',
  ...params,
]

export const CustomerGroupsView = () => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()
  const { customer } = useCustomerContext()
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [debouncedParams, setDebouncedParams] = useDebounce({} as GetExtranetCustomerGroupQueryDto)
  const [filteredGroups, setFilteredGroups] = useState<ExtranetCustomerGroupDto[]>([])

  const [params, setParams] = useState<GetExtranetCustomerGroupQueryDto & GroupSettingFilters>(
    mapCustomerGroupSearchParamsToQueryParams(searchParams)
  )

  useEffect(() => {
    if (searchInput !== debouncedParams.groupName) {
      setDebouncedParams((s) => ({ ...s, groupName: searchInput }))
    }
  }, [searchInput])

  const handleReset = () => {
    setSearchInput('')
    setSearchParams(createSearchParams({}))
  }

  const { data, isLoading } = useQuery({
    queryKey: __customerCustomerGroupsQueryKeyFactory(customer?.id, params.groupName),
    queryFn: async () =>
      await api.customers.getCustomerGroups(customer!.id!, {
        groupName: params.groupName,
      }),
  })

  const idFromFirstGroup = (data || [])[0]?.groupId

  useEffect(() => {
    setFilteredGroups(() => {
      const groups = data || []

      return groups.filter((customerGroup) => {
        let activeFilter = true

        if (params.active && !params.passive) {
          activeFilter = customerGroup.active
        }

        if (!params.active && params.passive) {
          activeFilter = !customerGroup.active
        }

        let scheduleSlotFilter = true

        if (
          params.scheduleSlotId &&
          customerGroup.scheduleSlot?.id !== Number(params.scheduleSlotId)
        ) {
          scheduleSlotFilter = false
        }

        if (params.scheduleSlotNotSet && customerGroup.scheduleSlot !== null) {
          scheduleSlotFilter = false
        }

        let productIdFilter = true

        if (params.productId && customerGroup.pricingProductId !== Number(params.productId)) {
          productIdFilter = false
        }

        if (params.productNotSet && customerGroup.pricingProductId !== null) {
          productIdFilter = false
        }

        return activeFilter && scheduleSlotFilter && productIdFilter
      })
    })
  }, [params, data, isLoading])

  const hasActiveFilters = () => {
    const params = mapCustomerGroupSearchParamsToQueryParams(searchParams)

    const hasNoSearchParams = Object.keys(params).length === 0

    return !hasNoSearchParams
  }

  useEffect(() => {
    setParams(() => mapCustomerGroupSearchParamsToQueryParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const noResults = !isLoading && data && data.length === 0

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        flexColumn
        paddingSmallHorizontal={!minDesktop}
        paddingLargeBottom
      >
        <ExtranetContainer
          full
          flexColumn
          paddingSmallHorizontal={!minDesktop}
        >
          <ExtranetViewHeader title={t('extranet:customerGroupSettings.title')}>
            <PharmacSearchWrapper>
              <PharmacSearch
                onChange={(v) => setSearchInput(v)}
                value={searchInput}
                placeholder={t('search', { ns: 'common' })}
                searchButtonContent={
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PharmacIcon
                      icon="search"
                      size="22"
                    />
                  </div>
                }
              />
              <div style={{ width: 'var(--spacing-small)' }} />
              <ExtranetFilters
                activeFilterCount={Object.keys(params).length}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={hasActiveFilters()}
                render={({ closeFilters }) => (
                  <CustomerGroupsFilters
                    closeFilters={closeFilters}
                    customerGroupId={idFromFirstGroup}
                  />
                )}
              />
            </PharmacSearchWrapper>
          </ExtranetViewHeader>
        </ExtranetContainer>
        <ExtranetContainer full>
          <ExtranetTable style={{ fontFamily: 'var(--font-family-secondary)', fontWeight: 300 }}>
            <ExtranetTableRow
              style={{
                backgroundColor: 'var(--extranet-light-purple)',
                border: 'none',
                fontWeight: 500,
              }}
            >
              <ExtranetTableColumn
                size={2}
                marginSmallLeft
              >
                {t('extranet:customerGroupSettings.customer')}
              </ExtranetTableColumn>
              {minDesktop && (
                <ExtranetTableColumn size={1.2}>
                  {t('extranet:customerGroupSettings.orderTime')}
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn>
                  <ExtranetContainer
                    full
                    flexRow
                    alignCenter
                  >
                    {t('extranet:customerGroupSettings.nextOrderDeadline')}

                    <PharmacPortalTooltip
                      paddingSmallLeft
                      placement="top"
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="20px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>
                        {t('extranet:customerGroupSettings.nextOrderDeadlineTooltip')}
                      </PharmacText>
                    </PharmacPortalTooltip>
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn>
                  {t('extranet:customerGroupSettings.doseDispensingFee')}
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn style={{ overflow: 'visible' }}>
                  <ExtranetContainer
                    full
                    flexRow
                    alignCenter
                  >
                    {t('extranet:customerGroupSettings.activity')}
                    <PharmacPortalTooltip
                      paddingSmallLeft
                      placement="top"
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="20px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>
                        {t('extranet:customerGroupSettings.activityTooltip')}
                      </PharmacText>
                    </PharmacPortalTooltip>
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              <ExtranetExpandArrowColumn />
            </ExtranetTableRow>
            <React.Fragment>
              {!noResults &&
                filteredGroups?.map((group) => (
                  <CustomerGroupsListItem
                    key={group.groupId}
                    customerGroup={group}
                    schedule={group.scheduleSlot}
                    pouchLayoutId={group.layoutId}
                  />
                ))}
              {noResults && <NoResultsResponse />}
            </React.Fragment>
          </ExtranetTable>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
